import { Button, Icon24ArrowRight, Typography } from "@cur8/maneki";
import { useStartCheckInMutation } from "hooks/mutations/useStartCheckInMutation";
import { usePatientQuery } from "hooks/queries/usePatientQuery";
import { useRoomQuery } from "hooks/queries/useRoomQuery";
import { useVisitQuery } from "hooks/queries/useVisitQuery";
import { useVisitHistoryQuery } from "hooks/queries/useVisitHistoryQuery";
import { generateVisitTypeLabel } from "lib/generateVisitTypeLabel";
import { getSafeReturnPath } from "lib/getSafeReturnPath";
import { CheckInParams } from "lib/types/checkInParameters";
import { useLocation } from "@pomle/react-router-paths";
import { paths } from "render/routes/paths";
import { ActionsFooter } from "render/ui/layout/ActionsFooter";
import { InformationLayout } from "render/ui/layout/InformationLayout";
import { PageFrameContent } from "render/ui/layout/PageFrameContent";
import { AppointmentCard } from "render/ui/presentation/AppointmentCard";
import styles from "./styles.module.sass";

export function SelectVisitConfirmView({
  goTo,
  onBack,
  patientId,
  visitId,
}: {
  goTo: (url: string) => void;
  onBack(): void;
} & CheckInParams) {
  const visitQuery = useVisitQuery({ patientId, visitId });
  const patientQuery = usePatientQuery({ patientId });
  const member = patientQuery.data;
  const visit = visitQuery.data;
  const roomQuery = useRoomQuery({ roomId: visit?.roomId });
  const room = roomQuery.data;

  const fullName = `${member?.name?.firstName} ${member?.name?.lastName}`;
  const time = `${visit?.startTime.toFormat(
    "HH:mm"
  )} - ${visit?.endTime.toFormat("HH:mm")}`;
  const roomName = room?.displayName;

  const { data: visitHistory } = useVisitHistoryQuery({ patientId });
  const visitTypeLabel =
    visit != null && visitHistory != null
      ? generateVisitTypeLabel({ visit, visitHistory })
      : undefined;

  const startCheckInMutation = useStartCheckInMutation();
  const location = useLocation();
  const nextUrl = getSafeReturnPath(
    location.searchParams.get("returnPath"),
    paths.manualCheckInConfirmed.url({ patientId, visitId })
  );

  return (
    <PageFrameContent>
      <InformationLayout
        content={
          <section className={styles.layout}>
            <div className={styles.title}>
              <Typography variant="display-s">Confirm check-in</Typography>
              <Typography variant="body-m" color="subtle">
                Confirm that this is the correct member.
              </Typography>
            </div>

            <AppointmentCard
              topLeft={fullName}
              topRight={roomName}
              bottomLeft={visitTypeLabel}
              bottomRight={time}
            />
          </section>
        }
        buttons={
          <ActionsFooter
            left={
              <Button variant="secondary" onPress={onBack} fillContainer>
                Back
              </Button>
            }
            right={
              <Button
                onPress={() => {
                  startCheckInMutation.mutate(
                    { patientId, visitId },
                    {
                      onSuccess: () => goTo(nextUrl),
                    }
                  );
                }}
                fillContainer
                icon={<Icon24ArrowRight />}
              >
                Continue
              </Button>
            }
          />
        }
      />
    </PageFrameContent>
  );
}
